import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type Me } from "./api";

interface SessionValue {
  readonly me: Me | null;
  readonly loading: boolean;
  refresh(): Promise<Me>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<Me>("/auth/me");
      setMe(next);
      return next;
    } catch {
      const anonymous: Me = { user: null, workspaces: [], currentWorkspaceId: null };
      setMe(anonymous);
      return anonymous;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    setMe({ user: null, workspaces: [], currentWorkspaceId: null });
  }, []);

  useEffect(() => {
    api
      .get<Me>("/auth/me")
      .then(setMe)
      .catch(() => setMe({ user: null, workspaces: [], currentWorkspaceId: null }))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo(() => ({ me, loading, refresh, signOut }), [me, loading, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

/** Where a signed-in person should land: onboarding until their workspace is set up. */
export function homeFor(me: Me | null): string {
  if (!me?.user) return "/signin";
  const current = me.workspaces.find((workspace) => workspace.id === me.currentWorkspaceId);
  if (!current) return "/onboarding";
  return current.onboarded ? "/app" : "/onboarding";
}
